import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Page = styled.div`
  padding: 1rem;
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1.25rem 0 0.8rem;
`;

const RefreshButton = styled.button`
  border: none;
  border-radius: 0.9rem;
  padding: 0.6rem 1rem;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  color: white;
  background: ${({ theme }) => theme.colors.primary};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SortTabs = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0;
  flex-wrap: wrap;
`;

const PeriodTabs = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0;
  flex-wrap: wrap;
`;

const SortTabButton = styled.button`
  border: 1px solid ${({ active, theme }) => (active ? theme.colors.primary : '#d1d5db')};
  background: ${({ active, theme }) => (active ? theme.colors.primary : 'white')};
  color: ${({ active }) => (active ? 'white' : '#374151')};
  border-radius: 9999px;
  padding: 0.42rem 0.85rem;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PeriodTabButton = styled.button`
  border: 1px solid ${({ active, theme }) => (active ? theme.colors.primary : '#d1d5db')};
  background: ${({ active, theme }) => (active ? theme.colors.primary : 'white')};
  color: ${({ active }) => (active ? 'white' : '#374151')};
  border-radius: 9999px;
  padding: 0.42rem 0.85rem;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
`;

const MonthPicker = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 0.65rem;
  padding: 0.42rem 0.6rem;
  font-size: 0.86rem;
  color: #374151;
  background: white;
`;

const Tab = styled.button`
  border: 2px solid ${({ active, theme }) => (active ? theme.colors.primary : 'transparent')};
  background: ${({ active, theme }) => (active ? 'rgba(102, 126, 234, 0.14)' : theme.colors.surfaceMuted)};
  color: ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.textPrimary)};
  border-radius: 1rem;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.8rem 0.9rem;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.9rem;
  margin-bottom: 1.2rem;

  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 1rem;
  padding: 1rem;
  text-align: center;
`;

const StatTitle = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
`;

const StatValue = styled.div`
  font-size: 1.85rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const Dashboard = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

const SectionCard = styled.section`
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 1rem;
  padding: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.8rem;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const SectionSub = styled.p`
  margin: -0.35rem 0 0.9rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.84rem;
`;

const PodiumStage = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`;

const PodiumColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
`;

const RankTag = styled.span`
  min-width: 2.1rem;
  height: 2.1rem;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ rank }) => (rank === 1 ? '#f59e0b' : rank === 2 ? '#9ca3af' : '#d97706')};
  color: #fff;
  font-size: 0.95rem;
  font-weight: 800;
`;

const PodiumBlock = styled.div`
  width: 100%;
  border-radius: 0.9rem 0.9rem 0.65rem 0.65rem;
  border: 2px solid #dbe3f0;
  background: white;
  min-height: ${({ rank }) => (rank === 1 ? '180px' : rank === 2 ? '145px' : '130px')};
  padding: 0.7rem 0.55rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 768px) {
    min-height: unset;
  }
`;

const Pedestal = styled.div`
  width: 100%;
  border-radius: 0.55rem;
  min-height: ${({ rank }) => (rank === 1 ? '58px' : rank === 2 ? '44px' : '36px')};
  background: ${({ rank }) =>
    rank === 1
      ? 'linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)'
      : rank === 2
        ? 'linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%)'
        : 'linear-gradient(180deg, #fdba74 0%, #ea580c 100%)'};
  color: white;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.35);
`;

const PlayerName = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 700;
  font-size: 1.02rem;
  text-align: center;
`;

const PlayerMeta = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.78rem;
  margin-top: 0.15rem;
`;

const Score = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.35rem;
  font-weight: 800;
  font-family: ${({ theme }) => theme.fonts.heading};
  text-align: center;
`;

const MedalEmoji = styled.span`
  font-size: 1.15rem;
  margin-right: 0.35rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  background: white;
  border-radius: 0.9rem;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
`;

const FilterLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 800;
  color: #4b5563;
  margin-right: 0.2rem;
`;

const TagGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.65rem;
`;

const TagCard = styled.div`
  background: white;
  border: 1px solid ${({ $highlight }) => ($highlight ? '#93c5fd' : '#e5e7eb')};
  border-radius: 0.8rem;
  padding: 0.65rem;
  box-shadow: ${({ $highlight }) => ($highlight ? '0 6px 20px rgba(37, 99, 235, 0.12)' : 'none')};
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${({ $highlight }) => ($highlight ? '4px' : '0')};
    background: #2563eb;
  }
`;

const TagName = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.3rem;
`;
const MeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.4rem;
  border-radius: 999px;
  padding: 0.1rem 0.4rem;
  background: #2563eb;
  color: #fff;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.03em;
`;

const StudentTag = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 0.18rem 0.52rem;
  font-size: 0.73rem;
  font-weight: 700;
  color: ${({ type }) => (type === 'speed' ? '#1d4ed8' : type === 'accuracy' ? '#047857' : '#7c2d12')};
  background: ${({ type }) => (type === 'speed' ? '#dbeafe' : type === 'accuracy' ? '#d1fae5' : '#ffedd5')};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 640px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Th = styled.th`
  text-align: left;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.82rem;
  font-weight: 700;
  padding: 0.72rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.95rem;
  padding: 0.78rem 0.72rem;
  border-bottom: 1px solid #eef2f7;
  white-space: nowrap;
  background: ${({ $highlight }) => ($highlight ? '#eff6ff' : 'transparent')};
  border-left: ${({ $highlight }) => ($highlight ? '3px solid #2563eb' : '3px solid transparent')};
`;

const Empty = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 2rem 1rem;
  font-size: 1rem;
  background: white;
  border-radius: 0.8rem;
`;

const getMedal = (rank) => {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return null;
};

const ErrorContainer = styled.div`
  background: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 0.8rem;
  color: #7f1d1d;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
`;

const practiceTypeOptions = [
    { value: 'position', label: '자리연습' },
    { value: 'word', label: '낱말연습' },
    { value: 'sentence', label: '단문연습' }
];

const periodOptions = [
  { value: 'all', label: '전체' },
  { value: 'last_week', label: '지난주' },
  { value: 'this_month', label: '이번달' },
  { value: 'month', label: '월별' }
];

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('ko-KR');
};

const formatDuration = (seconds) => {
  const safe = Number(seconds) || 0;
  const totalRounded = Math.round(safe * 100) / 100;
  const min = Math.floor(totalRounded / 60);
  const sec = Math.round((totalRounded - min * 60) * 100) / 100;
  const secDisplay = sec.toFixed(2).replace(/\.?0+$/, '');
  return `${min}분 ${secDisplay}초`;
};

const formatNumber2 = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return numeric.toFixed(2).replace(/\.?0+$/, '');
};

const formatPrimaryMetric = (player, sortBy) => {
  if (!player) return '-';
  if (sortBy === 'time') return formatDuration(player.duration_seconds || 0);
  if (sortBy === 'accuracy') return `${formatNumber2(player.accuracy || 0)}%`;
  return `${formatNumber2(player.typing_speed || 0)} 타/분`;
};

const TypingRankingPage = () => {
    const [rankings, setRankings] = useState([]);
    const [practiceRows, setPracticeRows] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentStudentId, setCurrentStudentId] = useState(null);
    const [selectedPracticeType, setSelectedPracticeType] = useState('sentence');
    const [selectedSortBy, setSelectedSortBy] = useState('speed');
    const [selectedPeriod, setSelectedPeriod] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const isPositionPractice = selectedPracticeType === 'position';
    const effectiveSortBy = isPositionPractice
      ? (selectedSortBy === 'time' ? 'time' : 'accuracy')
      : selectedSortBy;

    const fetchRankingData = useCallback(async (
      practiceType = selectedPracticeType,
      sortBy = effectiveSortBy,
      period = selectedPeriod,
      month = selectedMonth
    ) => {
        try {
            setLoading(true);
            setError('');
            const query = new URLSearchParams({
              practice_type: practiceType,
              sort_by: sortBy,
              period
            });
            if (period === 'month' && month) {
              query.set('month', month);
            }

            const response = await fetch(`${BACKEND_BASE_URL}/api/ranking?${query.toString()}`);
            if (!response.ok) {
                let detailMessage = '';
                try {
                  const errorPayload = await response.json();
                  detailMessage = errorPayload?.detail || errorPayload?.message || '';
                } catch (parseError) {
                  detailMessage = '';
                }
                throw new Error(detailMessage || '랭킹 데이터를 불러올 수 없습니다.');
            }
            const data = await response.json();
            setRankings(data.rankings || []);
            setStats(data.stats || {});

            const practiceTypeCandidates = ['position', 'word', 'sentence'];
            const practiceResponses = await Promise.all(
              practiceTypeCandidates.map((type) => {
                const sortForType = type === 'position' ? (sortBy === 'time' ? 'time' : 'accuracy') : sortBy;
                const practiceQuery = new URLSearchParams({
                  practice_type: type,
                  sort_by: sortForType,
                  period
                });
                if (period === 'month' && month) {
                  practiceQuery.set('month', month);
                }
                return fetch(`${BACKEND_BASE_URL}/api/ranking?${practiceQuery.toString()}`);
              })
            );
            const practicePayloads = await Promise.all(practiceResponses.map((res) => (res.ok ? res.json() : null)));
            setPracticeRows(practiceTypeCandidates.map((type, index) => {
              const top = practicePayloads[index]?.rankings?.[0];
              return {
                practiceType: type,
                label: type === 'position' ? '자리연습' : type === 'word' ? '낱말연습' : '단문연습',
                name: top?.name || '-',
                studentId: top?.student_id || null,
                metric: formatPrimaryMetric(
                  top,
                  type === 'position' ? (sortBy === 'time' ? 'time' : 'accuracy') : sortBy
                )
              };
            }));

        } catch (fetchError) {
            console.error('랭킹 데이터 불러오기 실패:', fetchError);
            setError(`랭킹 데이터를 불러오지 못했습니다. ${fetchError?.message || ''}`.trim());
        } finally {
            setLoading(false);
        }
    }, [selectedPracticeType, effectiveSortBy, selectedPeriod, selectedMonth]);

    useEffect(() => {
        fetchRankingData(selectedPracticeType, effectiveSortBy, selectedPeriod, selectedMonth);
    }, [fetchRankingData, selectedPracticeType, effectiveSortBy, selectedPeriod, selectedMonth]);

    useEffect(() => {
      const resolveCurrentStudentId = async () => {
        try {
          const raw = sessionStorage.getItem(PORTAL_USER_KEY);
          if (!raw) return;
          const portalUser = JSON.parse(raw);
          const loginId = portalUser?.login_id;
          if (!loginId) return;
          const response = await fetch(
            `${BACKEND_BASE_URL}/api/typing/student/records?login_id=${encodeURIComponent(loginId)}`
          );
          if (!response.ok) return;
          const payload = await response.json();
          setCurrentStudentId(payload?.student?.id || null);
        } catch {
          setCurrentStudentId(null);
        }
      };
      resolveCurrentStudentId();
    }, []);

    const topThree = useMemo(() => rankings.slice(0, 3), [rankings]);
    const periodTitleMap = {
      all: '전체',
      last_week: '지난주',
      this_month: '이번달',
      month: selectedMonth || '선택 월'
    };

  return (
    <Page>
      <Container>
        <SectionHeading
          eyebrow="Typing Ranking"
          title="타자 랭킹"
          description="같이 연습하는 친구들과 타자 기록을 나란히 보고, 나의 순위를 확인해 보세요."
        />
        <Header>
          <RefreshButton onClick={() => fetchRankingData(selectedPracticeType, effectiveSortBy, selectedPeriod, selectedMonth)} disabled={loading}>
            새로고침
          </RefreshButton>
        </Header>

        {error && <ErrorContainer>{error}</ErrorContainer>}

        <Tabs>
          {practiceTypeOptions.map(option => (
            <Tab
              key={option.value}
              active={selectedPracticeType === option.value}
              onClick={() => setSelectedPracticeType(option.value)}
              type="button"
            >
              {option.label}
            </Tab>
          ))}
        </Tabs>

        <Dashboard>
          <SectionCard>
            <SectionTitle>{periodTitleMap[selectedPeriod]} TOP 3</SectionTitle>
            <SectionSub>우승 단상 형태로 상위 3명을 보여줘요.</SectionSub>
            {loading ? (
              <Empty>불러오는 중...</Empty>
            ) : topThree.length > 0 ? (
              <PodiumStage>
                {[2, 1, 3].map((rank) => {
                  const player = topThree[rank - 1];
                  if (!player) return null;
                  return (
                    <PodiumColumn key={`${player.id}-${rank}`}>
                      <RankTag rank={rank}>{getMedal(rank) || rank}</RankTag>
                      <PodiumBlock rank={rank}>
                        <div>
                          <PlayerName>{player.name || '-'}</PlayerName>
                          <PlayerMeta>
                            {formatDate(player.date)} · 정확도 {formatNumber2(player.accuracy || 0)}%
                            {isPositionPractice ? ` · ${formatDuration(player.duration_seconds || 0)}` : ''}
                          </PlayerMeta>
                        </div>
                        <Score>
                          {effectiveSortBy === 'speed'
                            ? formatNumber2(player.typing_speed || 0)
                            : (effectiveSortBy === 'time' ? formatDuration(player.duration_seconds || 0) : `${formatNumber2(player.accuracy || 0)}%`)}
                        </Score>
                      </PodiumBlock>
                      <Pedestal rank={rank}>{rank}</Pedestal>
                    </PodiumColumn>
                  );
                })}
              </PodiumStage>
            ) : (
              <Empty>해당 조건의 기록이 아직 없어요.</Empty>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitle>Overview</SectionTitle>
            <SectionSub>현재 선택한 조건의 핵심 지표예요.</SectionSub>
            <StatGrid>
              <StatCard>
                <StatTitle>{isPositionPractice ? '평균 시간' : '평균 타수'}</StatTitle>
                <StatValue>{isPositionPractice ? formatDuration(stats.avg_duration_seconds || 0) : formatNumber2(stats.avg_speed || 0)}</StatValue>
              </StatCard>
              <StatCard>
                <StatTitle>평균 정확도</StatTitle>
                <StatValue>{formatNumber2(stats.avg_accuracy || 0)}%</StatValue>
              </StatCard>
              <StatCard>
                <StatTitle>참가 학생 수</StatTitle>
                <StatValue>{stats.total_students || 0}</StatValue>
              </StatCard>
              <StatCard>
                <StatTitle>현재 랭킹 인원</StatTitle>
                <StatValue>{rankings.length}</StatValue>
              </StatCard>
            </StatGrid>
          </SectionCard>

          <SectionCard>
            <SectionTitle>학생별 태그</SectionTitle>
            <SectionSub>상위 학생들의 강점을 빠르게 확인할 수 있어요.</SectionSub>
            <TagGrid>
              {rankings.slice(0, 8).map((player, index) => {
                const tagType = isPositionPractice
                  ? (player.accuracy >= 95 ? 'accuracy' : 'time')
                  : (player.typing_speed >= 400 ? 'speed' : player.accuracy >= 95 ? 'accuracy' : 'time');
                const tagLabel = tagType === 'speed' ? '빠름' : tagType === 'accuracy' ? '정확' : '효율';
                return (
                  <TagCard key={`${player.id}-${index}`} $highlight={currentStudentId === player.student_id}>
                    <TagName>
                      {player.name || '-'}
                      {currentStudentId === player.student_id && <MeBadge>ME</MeBadge>}
                    </TagName>
                    <StudentTag type={tagType}>{tagLabel}</StudentTag>
                  </TagCard>
                );
              })}
            </TagGrid>
            {!loading && rankings.length === 0 && <Empty>학생 태그를 표시할 데이터가 없습니다.</Empty>}
          </SectionCard>

          <SectionCard>
            <SectionTitle>연습유형별 순위</SectionTitle>
            <SectionSub>같은 기간/정렬 기준에서 연습 유형별 1위를 비교해요.</SectionSub>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>연습 유형</Th>
                    <Th>1위 닉네임</Th>
                    <Th>대표 기록</Th>
                  </tr>
                </thead>
                <tbody>
                  {practiceRows.map((row) => (
                    <tr key={row.practiceType}>
                      <Td $highlight={currentStudentId === row.studentId}>{row.label}</Td>
                      <Td $highlight={currentStudentId === row.studentId}>
                        {row.name}
                        {currentStudentId === row.studentId && <MeBadge>ME</MeBadge>}
                      </Td>
                      <Td $highlight={currentStudentId === row.studentId}>{row.metric}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {!loading && practiceRows.length === 0 && <Empty>연습유형별 데이터를 표시할 수 없습니다.</Empty>}
            </TableWrap>
          </SectionCard>

          <SectionCard>
            <SectionTitle>전체 데이터</SectionTitle>
            <SectionSub>아이들별 최고기록 1개씩 보여줘요. 아래에서 기존 필터도 함께 확인할 수 있어요.</SectionSub>
            <FilterRow>
              <FilterLabel>타자 필터 |</FilterLabel>
              <SortTabs>
                {!isPositionPractice && (
                  <SortTabButton
                    type="button"
                    active={selectedSortBy === 'speed'}
                    onClick={() => setSelectedSortBy('speed')}
                  >
                    타수순 보기
                  </SortTabButton>
                )}
                <SortTabButton
                  type="button"
                  active={effectiveSortBy === 'accuracy'}
                  onClick={() => setSelectedSortBy('accuracy')}
                >
                  정확도순 보기
                </SortTabButton>
                <SortTabButton
                  type="button"
                  active={effectiveSortBy === 'time'}
                  onClick={() => setSelectedSortBy('time')}
                >
                  시간순 보기
                </SortTabButton>
              </SortTabs>
            </FilterRow>

            <FilterRow>
              <FilterLabel>기간 필터 |</FilterLabel>
              <PeriodTabs>
                {periodOptions.map(option => (
                  <PeriodTabButton
                    key={option.value}
                    type="button"
                    active={selectedPeriod === option.value}
                    onClick={() => setSelectedPeriod(option.value)}
                  >
                    {option.value === 'month' ? '월선택' : option.label}
                  </PeriodTabButton>
                ))}
              </PeriodTabs>
              {selectedPeriod === 'month' && (
                <MonthPicker
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                />
              )}
            </FilterRow>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>순위</Th>
                    <Th>닉네임</Th>
                    <Th>{isPositionPractice ? '시간' : '타수'}</Th>
                    <Th>정확도</Th>
                    <Th>기록일</Th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.map((player, index) => (
                    <tr key={`${player.id}-${index}`}>
                      <Td $highlight={currentStudentId === player.student_id}>
                        {getMedal(index + 1) ? (
                          <>
                            <MedalEmoji>{getMedal(index + 1)}</MedalEmoji>
                            {index + 1}
                          </>
                        ) : (
                          index + 1
                        )}
                      </Td>
                      <Td $highlight={currentStudentId === player.student_id}>
                        {player.name || '-'}
                        {currentStudentId === player.student_id && <MeBadge>ME</MeBadge>}
                      </Td>
                      <Td $highlight={currentStudentId === player.student_id}>
                        {isPositionPractice ? formatDuration(player.duration_seconds || 0) : `${formatNumber2(player.typing_speed || 0)} 타/분`}
                      </Td>
                      <Td $highlight={currentStudentId === player.student_id}>{formatNumber2(player.accuracy || 0)}%</Td>
                      <Td $highlight={currentStudentId === player.student_id}>{formatDate(player.date)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {!loading && rankings.length === 0 && <Empty>표시할 전체 데이터가 없습니다.</Empty>}
            </TableWrap>
          </SectionCard>
        </Dashboard>
      </Container>
    </Page>
  );
};

export default TypingRankingPage;

